/* @flow */

import type { BasicFunction, KeyValuePair } from '../../helpers/jsHelpers/types';
import type { NETGEM_API_DMS_REQUEST_RESPONSE_CHALLENGE } from '../../libs/netgemLibrary/dms/types/RequestResponseChallenge';
import type { NETGEM_API_V8_REQUEST_RESPONSE } from '../../libs/netgemLibrary/v8/types/RequestResponse';
import type { AppConfigurationFeatures } from '../../redux/appConf/types/types';

import { FEATURE_SUBSCRIPTION, type FEATURE_TYPE, FEATURE_VOD } from '../../redux/appConf/constants';

export const Padding = Object.freeze({
  Duration: 10,
  InitSequence: 15,
  Name: 40,
  StepName: 27,
  Version: 31,
  VersionBO: 28,
  VersionCrmBack: 22,
  VersionCrmFront: 21,
});

export const Css = Object.freeze({
  Initial: 'background-color: unset; color: unset; font-weight: normal',
  StepError: 'background-color: #F00; color: #000; font-weight: bold',
  StepSuccess: 'background-color: #0F0; color: #000; font-weight: bold',
});

// Wait two minutes at startup before declaring failure (in milliseconds)
export const STARTING_TIMEOUT = 120_000;

export enum QueryStatus {
  NotStarted = '',
  Disabled = 'disabled',
  Error = 'error',
  InProgress = 'inProgress',
  Success = 'success',
}

export type STEP_ITEM = {
  name: string,
  parameters?: Array<'applicationId' | 'deviceKey'>,
  request: any,
  then?: Array<STEP_ITEM>,
  thenAfter?: Array<STEP_ITEM>,
};

export type STEP_TYPE = {
  displayIndex: number,
  displayName: string,
  feature?: FEATURE_TYPE,
  isOptional?: boolean,
  name: string,
};

export const StepName = Object.freeze({
  ChannelList: 'channelList',
  ChannelsInfo: 'channelsInfo',
  CommercialOffers: 'commercialOffers',
  ContinuousWatchingHub: 'continuousWatchingHub',
  DefaultHub: 'defaultHub',
  DefaultRights: 'defaultRights',
  DeviceSettings: 'deviceSettings',
  Discovery: 'discovery',
  Hub: 'hub',
  ListAlias: 'listAlias',
  Login: 'login',
  TagListAliases: 'tagListAliases',
  UserRights: 'userRights',
  VideofuturAuthentication: 'boAuthentication',
  VideofuturDiscovery: 'boDiscovery',
  VideofuturPersonalData: 'boPersonalData',
  VideofuturStopOpenStreams: 'boStopOpenStreams',
});

export type STEP_SET = KeyValuePair<STEP_TYPE>;

// Define all existing startup steps
export const STEPS: Readonly<STEP_SET> = Object.freeze({
  [StepName.ChannelList]: {
    displayIndex: 2,
    displayName: 'DMS Channel List',
    name: StepName.ChannelList,
  },
  [StepName.ChannelsInfo]: {
    displayIndex: 5,
    displayName: 'PTF Channels Info',
    name: StepName.ChannelsInfo,
  },
  [StepName.CommercialOffers]: {
    displayIndex: 10,
    displayName: 'PTF Commercial Offers',
    feature: FEATURE_SUBSCRIPTION,
    isOptional: true,
    name: StepName.CommercialOffers,
  },
  [StepName.ContinuousWatchingHub]: {
    displayIndex: 9,
    displayName: 'PTF Continuous Watching Hub',
    name: StepName.ContinuousWatchingHub,
  },
  [StepName.DefaultHub]: {
    displayIndex: 8,
    displayName: 'PTF Default Hub',
    name: StepName.DefaultHub,
  },
  [StepName.DefaultRights]: {
    displayIndex: 11,
    displayName: 'PTF Default Rights',
    feature: FEATURE_SUBSCRIPTION,
    isOptional: true,
    name: StepName.DefaultRights,
  },
  [StepName.DeviceSettings]: {
    displayIndex: 1,
    displayName: 'DMS Device Settings',
    name: StepName.DeviceSettings,
  },
  [StepName.Discovery]: {
    displayIndex: 3,
    displayName: 'PTF Discovery',
    name: StepName.Discovery,
  },
  [StepName.Hub]: {
    displayIndex: 7,
    displayName: 'PTF Hub',
    name: StepName.Hub,
  },
  [StepName.ListAlias]: {
    displayIndex: 4,
    displayName: 'PTF Channel List Alias',
    name: StepName.ListAlias,
  },
  [StepName.Login]: {
    displayIndex: 0,
    displayName: 'DMS Login',
    name: StepName.Login,
  },
  [StepName.TagListAliases]: {
    displayIndex: 6,
    displayName: 'PTF Tag List Aliases',
    name: StepName.TagListAliases,
  },
  [StepName.UserRights]: {
    displayIndex: 12,
    displayName: 'PTF User Rights',
    feature: FEATURE_SUBSCRIPTION,
    isOptional: true,
    name: StepName.UserRights,
  },
  [StepName.VideofuturAuthentication]: {
    displayIndex: 13,
    displayName: 'BO Login',
    feature: FEATURE_VOD,
    isOptional: true,
    name: StepName.VideofuturAuthentication,
  },
  [StepName.VideofuturDiscovery]: {
    displayIndex: 14,
    displayName: 'BO Discovery',
    feature: FEATURE_VOD,
    isOptional: true,
    name: StepName.VideofuturDiscovery,
  },
  [StepName.VideofuturPersonalData]: {
    displayIndex: 15,
    displayName: 'BO Personal Data',
    feature: FEATURE_VOD,
    isOptional: true,
    name: StepName.VideofuturPersonalData,
  },
  [StepName.VideofuturStopOpenStreams]: {
    displayIndex: 16,
    displayName: 'BO Kill Streams (<count>)',
    feature: FEATURE_VOD,
    isOptional: true,
    name: StepName.VideofuturStopOpenStreams,
  },
});

export type ReduxStartReducerStateType = {
  +applicationId: string,
  +applicationName: string,
  +authDeviceUrl: string,
  +authenticationToken: string | null,
  +deviceKey: string,
  +features: AppConfigurationFeatures,
  +isDebugModeEnabled: boolean,
  +isRegisteredAsGuest: boolean,
  +subscriberId: string,
  +upgradeDeviceUrl: string,
  +versionBO: string,
  +versionCrmBack: string,
  +versionCrmFront: string,
  +versionShort: string,
};

export type ReduxStartDispatchToPropsType = {
  +localCreateTagListAliases: (signal: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE | null>,
  +localSendCommercialOfferRequest: (signal: AbortSignal) => Promise<void>,
  +localSendContinuousWatchingHubRequest: (signal: AbortSignal) => Promise<void>,
  +localSendDefaultHubRequest: (signal: AbortSignal) => Promise<void>,
  +localSendDefaultRightsRequest: (signal: AbortSignal) => Promise<void>,
  +localSendDiscoveryRequest: (signal: AbortSignal) => Promise<void>,
  +localSendGetDeviceChannelListRequest: (signal: AbortSignal) => Promise<void>,
  +localSendGetDeviceSettingRequest: (signal: AbortSignal) => Promise<void>,
  +localSendHubRequest: (signal: AbortSignal) => Promise<void>,
  +localSendListAliasPostRequest: (signal: AbortSignal) => Promise<void>,
  +localSendLoginRequest: (
    authDeviceUrl: string,
    upgradeDeviceUrl: string,
    applicationId: string,
    subscriberId: string,
    deviceKey: string,
    signal?: AbortSignal,
  ) => Promise<NETGEM_API_DMS_REQUEST_RESPONSE_CHALLENGE>,
  +localSendMetadataChannelsRequest: (signal: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE | null>,
  +localSendUserRightsRequest: (signal: AbortSignal) => Promise<void>,
  +localSendVideofuturAuthenticationRequest: (applicationId: string, deviceKey: string, signal: AbortSignal) => Promise<void>,
  +localSendVideofuturDiscoveryRequest: (signal: AbortSignal) => Promise<void>,
  +localSendVideofuturPersonalDataRequest: (signal: AbortSignal) => Promise<void>,
  +localStopVideofuturOpenStreams: (signal: AbortSignal) => Promise<number>,
  +localToggleDebugMode: BasicFunction,
};

export type StartPropType = {
  +isLoadingAnimationVisible: boolean,
  +failureCallback: BasicFunction,
  +successCallback: BasicFunction,
  +timeoutCallback: BasicFunction,
  +unrecoverableFailureCallback: BasicFunction,
};

export type CompleteStartPropType = {
  ...StartPropType,
  ...ReduxStartReducerStateType,
  ...ReduxStartDispatchToPropsType,
};

export type StartStateType = {
  currentSteps: Array<STEP_ITEM>,
  durations: KeyValuePair<string>,
  isDebugPanelVisible: boolean | null,
  killedStreamCount: string,
  nextSteps: Array<STEP_ITEM>,
  queryStatuses: KeyValuePair<QueryStatus>,
  stepProgress: number,
};
